import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { TrackClick } from '@g4/trackster';
import { getPrice } from '@g4/next-furniture/Price/Price';
import isNumber from 'lodash/isNumber';
import { withValue } from '@g4/automation-hooks';
import { waterBlue } from '@g4/boneyard/lib/theme/colors';

import { useTranslations } from '../../../../graphql/components/TranslationsProvider';
import {
  sortAncillariesByOrder,
  ancillarySortOrder,
} from '../../../../utils/ancillaryUtils.js';

import {
  Box,
  Text,
  useMedia,
  Button,
  ConditionalWrap,
} from '../../../Boneyard';
import Markdown from '../../../Markdown';
import TripSummary from '../../../../pageComponents/paymentPage/TripSummary.js';
import { tripflexCode, ancillaryTypes } from '../../../../pageComponents/ancillariesPage/constants.js';

const BUNDLE_TIERS = [1, 2, 3];
// TEMP
const DEFAULT_BUNDLE_TIER = 2;

const propTypes = {
  bundle: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.any,
    // TODO mock data sort
    // tier: PropTypes.oneOf(BUNDLE_TIERS).isRequired,
    tier: PropTypes.number,
    ancillaries: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
    banner: PropTypes.string,
  }).isRequired,
  theme: PropTypes.shape({
    colors: PropTypes.shape({
      bundle: PropTypes.object.isRequired,
      common: PropTypes.object.isRequired,
    }),
  }),
  bundlePrice: PropTypes.number,
  isTripSummary: PropTypes.bool,
  isStadium: PropTypes.bool,
  automationHook: PropTypes.string,
};

const BundleLogo = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${(props) => props.imgSrc});
  background-size: 150%;
  background-position: center;
  background-repeat: no-repeat;
`;

const BundleRibbon = styled.div`
  background: ${(props) => props.bgColor};
  margin-right: ${(props) => props.theme.spacing(5)};
  height: 50px;
  width: ${(props) =>
    props.isTripSummary ? (props.isMobile ? '80px' : '100px') : '90px'};
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  border-radius: 8px;

  @media (max-width: 768px) {
    margin-right: ${(props) => props.theme.spacing(2)};
    height: 40px;
    width: ${(props) => (props.isTripSummary ? '85px' : '90px')};
  }
`;

const BundleButton = styled(Button)`
  width: 260px;
`;

const AncillaryWithIconBox = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin-right: 8px;
  margin-bottom: 6px;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 768px) {
    margin-right: 4px;
    margin-bottom: 4px;
  }
`;

const AncillariesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  max-width: 100%;
  gap: 6px 20px;
  margin-left: 20px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 4px 10px;
    margin-left: 10px;
  }

  @media (max-width: 600px) {
    gap: 2px 8px;
    margin-left: 8px;
  }

  @media (max-width: 450px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const AncillaryImage = styled.img`
  margin-right: 6px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-right: 4px;
    width: 16px;
    height: 16px;
  }

  @media (max-width: 600px) {
    margin-right: 3px;
    width: 14px;
    height: 14px;
  }
`;

const RefundPill = styled.div`
  box-sizing: border-box;
  border: 1px solid #00579B;
  border-radius: 18px;
  padding: 5px 6px;
  width: fit-content;
  background-color: #F0F4FB;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  font-size: 10px;
  font-weight: 500;
  font-family: Montserrat;
  text-align: center;
  color: #005695;
  line-height: 12px;

  @media (max-width: 1200px) {
    white-space: normal;
  }
`;

/**
 * Transform ancillary display names based on context
 * @param {string} ancillaryName - Original ancillary name
 * @param {number} bundleTier - Bundle tier (1=Basic, 2=Bonus, 3=Total)
 * @param {Object} cartTranslations - Cart translations object
 * @returns {string|Object} Transformed display name or translation object for HTML
 */
const getAncillaryDisplayName = (ancillaryName, bundleTier, cartTranslations) => {

  const displayNameMap = {
    'Priority Access': cartTranslations?.priorityAccessOptionLabelHTML,
    'Trip Flex': cartTranslations?.tripFlexOptionLabelHTML,
    'Checked Bag': cartTranslations?.oneCheckedBagOptionLabelHTML,
    'Carry-On': cartTranslations?.oneCarryOnBagOptionLabelHTML,
    'One personal item': cartTranslations?.onePersonalItemOptionLabelHTML,
    'Enhanced Trip Flex': cartTranslations?.enhancedTripFlexOptionLabelHTML
  };

  if (ancillaryName === 'Seat Selection') {
    if (bundleTier === 1) {
      return cartTranslations?.basicBundleSeatsOptionLabelHTML
    }
    if (bundleTier === 2) {
      return cartTranslations?.bonusBundleSeatsOptionLabelHTML
    }
    return cartTranslations?.totalBundleSeatsOptionLabelHTML
  }

  return displayNameMap[ancillaryName] || ancillaryName;
};

/**
 * Separate ancillaries into two groups
 * Group 1: "One personal item", "Checked Bag", "Carry-On"
 * Group 2: All remaining items
 * @param {Array} ancillaries - Array of ancillary objects
 * @returns {Object} Object with firstGroup and secondGroup arrays
 */
const separateAncillariesIntoGroups = (ancillaries) => {
  const firstGroupNames = ['One personal item', 'Checked Bag', 'Carry-On'];
  const firstGroup = [];
  const secondGroup = [];

  ancillaries.forEach((ancillary) => {
    if (firstGroupNames.includes(ancillary.name)) {
      firstGroup.push(ancillary);
    } else {
      secondGroup.push(ancillary);
    }
  });

  return { firstGroup, secondGroup };
};

const BundleInfo = ({
  bundle,
  theme,
  bundlePrice,
  isTripSummary,
  automationHook,
  isStadium,
}) => {
  const mq = useMedia();
  const isMobile = mq.lte('md');
  const { price } = getPrice(bundlePrice);

  const translations = useTranslations();
  const { addBundleButtonLabelMobile, addBundleButtonLabelDesktop } =
    isTripSummary ? translations.paymentPage : translations.cart;

  const {tripFlexOFOPLabelHTML} = translations.cart;

  const { banner, tier, ancillaries, name } = bundle;

  const sortedAncillaries = sortAncillariesByOrder(ancillaries, ancillarySortOrder);

  const ancillaryNames = sortedAncillaries.map((ancillary) => ancillary.name);
  const ancillariesListString = ancillaryNames.join(' | ');

  // Separate ancillaries into two groups for display
  const { firstGroup, secondGroup } = separateAncillariesIntoGroups(sortedAncillaries);

  // TEMP default to tier 2 (mock data issue)
  const bundleTier = BUNDLE_TIERS.includes(tier) ? tier : DEFAULT_BUNDLE_TIER;
  const bgColor =
    theme.colors.bundle[`tier_${bundleTier}`] &&
    theme.colors.bundle[`tier_${bundleTier}`].label;

  return (
    <Box
      backgroundColor={theme.colors.common.light}
      d="flex"
      alignItems="center"
    >
      {banner && !(!isTripSummary && mq.lt('md')) && (
        <BundleRibbon
          bgColor={bgColor}
          isMobile={isMobile}
          isTripSummary={isTripSummary}
        >
          <BundleLogo imgSrc={banner} />
        </BundleRibbon>
      )}

      {ancillaries.length ? (
        <ConditionalWrap
          condition={isTripSummary}
          wrap={(children) => (
            <Box w="100%" pr={isMobile ? 1 : 0}>
              {children}
            </Box>
          )}
        >
          {!isStadium || isTripSummary ? (
            <Box d="flex" alignItems="center" w="100%">
              {isTripSummary && !mq.lt('md') ? (
                <>
                  <Box flex="1" pr={2} minWidth={0}>
                    {/* First group: One personal item, Checked Bag, Carry-On */}
                    {firstGroup.length > 0 && (
                      <AncillariesGrid>
                        {firstGroup.map((ancillary, idx) => (
                          <AncillaryWithIconBox key={idx}>
                            {ancillary.icon && (
                              <AncillaryImage
                                src={ancillary.icon}
                                alt={ancillary.name}
                              />
                            )}
                            <Text
                              size={mq.lte('sm') ? 10 : mq.lte('md') ? 12 : 14}
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                            >
                              <Markdown source={getAncillaryDisplayName(
                                  ancillary.name,
                                  bundleTier,
                                  translations.cart
                                )} />
                            </Text>
                          </AncillaryWithIconBox>
                        ))}
                      </AncillariesGrid>
                    )}

                    {/* Second group: Remaining items */}
                    {secondGroup.length > 0 && (
                      <AncillariesGrid>
                        {secondGroup.map((ancillary, idx) => (
                          <AncillaryWithIconBox style = {{alignItems: 'flex-start'}} key={idx}>
                            {ancillary.icon && (
                              <AncillaryImage style = {{marginTop: '2px'}}
                                src={ancillary.icon}
                                alt={ancillary.name}
                              />
                            )}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                            <Text
                              size={mq.lte('sm') ? 10 : mq.lte('md') ? 12 : 14}
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                              >
                                <Markdown source={getAncillaryDisplayName(
                                  ancillary.name,
                                  bundleTier,
                                  translations.cart
                                )} />
                              </Text>
                              {ancillary.code === tripflexCode.ENHANCED_TRIP_FLEX && ancillary.type === ancillaryTypes.TRIP_FLEX && (
                                <RefundPill style={{ marginTop: '4px' }}>
                                  <Markdown source={tripFlexOFOPLabelHTML} />
                                </RefundPill>
                              )}
                            </div>
                          </AncillaryWithIconBox>
                        ))}
                      </AncillariesGrid>
                    )}
                  </Box>
                  <Box flexShrink={0} ml={2}>
                    <Text
                      automationHook={withValue(automationHook, 'value')}
                      size={mq.lte('sm') ? 16 : mq.lte('md') ? 18 : 20}
                      weight={300}
                    >
                      {price}
                    </Text>
                  </Box>
                </>
              ) : (
                <>
                  <Box maxWidth={mq.lt('md') && !isTripSummary ? 'calc(100% - 60px)' : null}>
                    <Text
                      size={mq.lt('md') ? 14 : 13}
                      lh={1.2}
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ancillariesListString}
                    </Text>
                  </Box>
                  {!mq.lt('md') && (
                    <Box ml={2} flexShrink={0}>
                      <Text
                        automationHook={withValue(automationHook, 'value')}
                        size={20}
                        weight={500}
                      >
                        {price}
                      </Text>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <Text size={15}>{'Included'}</Text>
          )}
        </ConditionalWrap>
      ) : (
        <TrackClick
          category="Site Click Tracking"
          action="Trip Summary"
          label={addBundleButtonLabelMobile}
        >
          <BundleButton
            onClick={() => {
              alert('go to bundles page');
            }}
            type="secondary"
          >
            {mq.lte('md')
              ? addBundleButtonLabelMobile
              : addBundleButtonLabelDesktop}
          </BundleButton>
        </TrackClick>
      )}
    </Box>
  );
};

BundleInfo.propTypes = propTypes;

export default withTheme(BundleInfo);
